import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  Toolbar,
  Typography,
  Card,
  CircularProgress,
  Backdrop,
  Alert,
  AlertTitle,
  CssBaseline,
  Button,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Paper,
  Divider,
  Grid,
  Stack,
  LinearProgress,
  Container,
  useMediaQuery,
  CardContent,
  StepConnector,
} from "@mui/material";
import { styled as muiStyled, ThemeProvider, alpha } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { useQuery } from "react-query";
import {
  CheckCircle as CheckCircleIcon,
  PersonOutline as PersonIcon,
  LinkOutlined as LinkIcon,
  VerifiedUser as VerifiedIcon,
  Celebration as CelebrationIcon,
  Info as InfoIcon,
} from "@mui/icons-material";

import HomeHeader from "../../Components/HomeHeader";
import AlertDialog from "../../Components/AlertDialog.js";
import useAlertDialog from "../../Components/useAlertDialog.js";
import API from "../../API/index.js";
import { lightTheme } from "../../Utils/baseTheme.js";
import routes from "../../Config/routes.js";
import MuiPhoneInput from "../../Components/MuiPhoneInput";

// Custom Stepper Connector
const ColorlibConnector = muiStyled(StepConnector)(({ theme }) => ({
  [`&.Mui-active`]: {
    [`& .MuiStepConnector-line`]: {
      backgroundImage: `linear-gradient(95deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    },
  },
  [`&.Mui-completed`]: {
    [`& .MuiStepConnector-line`]: {
      backgroundImage: `linear-gradient(95deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    },
  },
  [`& .MuiStepConnector-line`]: {
    height: 3,
    border: 0,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderRadius: 1,
  },
  [`&.MuiStepConnector-alternativeLabel`]: {
    top: 22,
  },
}));

// Custom Step Icon
const ColorlibStepIconRoot = muiStyled('div')(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  ...(ownerState.active && {
    backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  }),
  ...(ownerState.completed && {
    backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, className } = props;

  const icons = {
    1: <VerifiedIcon />,
    2: <PersonIcon />,
    3: <LinkIcon />,
    4: <CelebrationIcon />,
  };

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      {icons[String(props.icon)]}
    </ColorlibStepIconRoot>
  );
}

const CreatorConnect = () => {
  const navigate = useNavigate();
  const { creator: creatorParam } = useParams();
  const formRef = useRef(null);
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const theme = lightTheme;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Persist creatorName from URL param to sessionStorage (one-time on mount)
  const initialCreatorName =
    sessionStorage.getItem("creatorName") || creatorParam || "";
  const [creatorName, setCreatorName] = useState(initialCreatorName);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const [profileData, setProfileData] = useState({
    phone_number: "",
    region: "NAUKAUS",
    niche: [],
    socialLinks: {
      youtube: "",
      instagram: "",
      tiktok: "",
      twitch: "",
      x: "",
      podcast: "",
      kick: "",
    },
  });

  useEffect(() => {
    if (creatorParam && !sessionStorage.getItem("creatorName")) {
      sessionStorage.setItem("creatorName", creatorParam);
      setCreatorName(creatorParam);
    }
  }, [creatorParam]);

  const {
    data: alreadyRegistered,
    isLoading: isVerifying,
    isError,
    error,
  } = useQuery({
    queryKey: ["check_registration", creatorName],
    queryFn: () => API.creatorConnect.verify(creatorName),
    enabled: !!creatorName,
  });

  // Pre-populate data from existing creator record if available
  useEffect(() => {
    if (alreadyRegistered?.existsInCreatorDB && alreadyRegistered?.creator) {
      const creatorRecord = alreadyRegistered.creator;
      setProfileData((prev) => ({
        ...prev,
        phone_number: creatorRecord.phone_number || "",
        region: creatorRecord.region || "NAUKAUS",
        niche: creatorRecord.primary_markets || [],
        socialLinks: {
          youtube: creatorRecord.youtube_link || "",
          instagram: creatorRecord.instagram_link || "",
          tiktok: creatorRecord.tiktok_link || "",
          twitch: creatorRecord.twitch_link || "",
          x: creatorRecord.x_link || "",
          podcast: creatorRecord.podcast_link || "",
          kick: creatorRecord.kick_link || "",
        },
      }));
    }
  }, [alreadyRegistered]);

  // Calculate completion percentage
  const completionPercentage = useMemo(() => {
    const step1 = creatorName ? 25 : 0;
    const phoneValid = profileData.phone_number.length >= 11;
    const step2 = (phoneValid && profileData.region && profileData.niche.length > 0) ? 25 : 0;
    const step3 = Object.values(profileData.socialLinks).some(link => link) ? 25 : 0;
    const step4 = 0; // Will be 25 when Google auth completes
    return step1 + step2 + step3 + step4;
  }, [creatorName, profileData]);

  // Validation for step 2
  const isStep2Valid = useMemo(() => {
    return profileData.phone_number.length >= 11 && 
           profileData.region && 
           profileData.niche.length > 0;
  }, [profileData]);

  const regions = [
    { value: "NAUKAUS", label: "North America / UK / Australia" },
    { value: "EURO", label: "Europe" },
    { value: "ASIA", label: "Asia Pacific" },
    { value: "LATAM", label: "Latin America" },
    { value: "OTHER", label: "Other" },
  ];

  const availableNiches = [
    "Gaming",
    "Beauty",
    "Fashion",
    "Tech",
    "Food",
    "Travel",
    "Fitness",
    "Lifestyle",
    "Comedy",
    "Education",
    "Music",
    "Art",
    "Business",
    "Sports",
    "Automotive",
    "Home & Garden",
    "Parenting",
    "Finance",
    "Health",
  ];

  const socialPlatforms = [
    { key: "youtube", label: "YouTube", placeholder: "https://youtube.com/@username", icon: "🎥" },
    { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/username", icon: "📸" },
    { key: "tiktok", label: "TikTok", placeholder: "https://tiktok.com/@username", icon: "🎵" },
    { key: "twitch", label: "Twitch", placeholder: "https://twitch.tv/username", icon: "🎮" },
    { key: "x", label: "X (Twitter)", placeholder: "https://x.com/username", icon: "🐦" },
    { key: "podcast", label: "Podcast", placeholder: "https://your-podcast.com", icon: "🎙️" },
    { key: "kick", label: "Kick", placeholder: "https://kick.com/username", icon: "⚡" },
  ];

  const steps = ["Verify Identity", "Build Profile", "Connect Socials", "Secure Account"];

  // Google login success handler
  const handleCredentialResponse = async (response) => {
    setLoading(true);
    try {
      const payload = {
        username: creatorName,
        google_token: response.credential,
        status: "Associate",
        ...profileData,
      };

      await API.creatorConnect.connect(payload);

      // Attempt auto-login using unilogin_v2
      try {
        const loginResponse = await fetch("/api/users/unilogin_v2", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: response.credential }),
        });

        const loginResult = await loginResponse.json();

        if (loginResponse.ok && loginResult.tokens?.creator) {
          localStorage.setItem("token", loginResult.tokens.creator);
          if (loginResult.profiles?.creator) {
            localStorage.setItem("user", JSON.stringify(loginResult.profiles.creator));
          }

          const go = () => {
            if (loginResult.redirect === "edit") {
              navigate("/creatorconnect/edit");
            } else {
              navigate("/creatorconnect/dashboard");
            }
          };

          openDialog(
            "Success",
            "Welcome to Blitz! Your account has been created and you're now logged in.",
            go,
            go,
            "Continue to Dashboard",
            null
          );
        } else {
          openDialog(
            "Success",
            "You have successfully claimed your account!",
            () => navigate(routes.creatorLogin),
            () => navigate(routes.creatorLogin),
            "Go to Creator Login",
            null
          );
        }
      } catch (loginError) {
        console.error("Auto-login failed:", loginError);
        openDialog(
          "Success",
          "You have successfully claimed your account!",
          () => navigate(routes.creatorLogin),
          () => navigate(routes.creatorLogin),
          "Go to Creator Login",
          null
        );
      }
    } catch (err) {
      console.error("Error during form submission:", err);
      openDialog(
        "Error",
        `There was an error: ${err?.response?.data?.error || err.message}`,
        closeDialog,
        closeDialog,
        "Ok",
        null
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLoginFailure = (error) => {
    console.error("Failed to login with Google:", error);
    setLoading(false);
    openDialog(
      "Error",
      <>Google login failed, please try again.<br />Error: {String(error)}</>,
      closeDialog,
      closeDialog,
      "Ok",
      null
    );
  };

  const handleNext = () => {
    // Validate step 2 before proceeding
    if (activeStep === 1 && !isStep2Valid) {
      openDialog(
        "Incomplete Profile",
        "Please complete all required fields: phone number (valid format), region, and at least one content category.",
        closeDialog,
        closeDialog,
        "Ok",
        null
      );
      return;
    }
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => setActiveStep((s) => s - 1);

  const updateProfileData = (field, value) =>
    setProfileData((prev) => ({ ...prev, [field]: value }));

  const updateSocialLink = (platform, value) =>
    setProfileData((prev) => ({
      ...prev,
      socialLinks: { ...prev.socialLinks, [platform]: value },
    }));

  const formIsDisabled =
    alreadyRegistered?.registered || alreadyRegistered?.existsInCreatorDB === false;

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ py: isMobile ? 2 : 4 }}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                }}
              >
                <VerifiedIcon sx={{ fontSize: 48, color: "white" }} />
              </Box>
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} gutterBottom>
                Welcome to Blitz! 🎉
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: "auto", mb: 4 }}>
                You've been invited to claim your creator account. This process takes just a few minutes
                and unlocks access to brand partnerships, automated payments, and campaign management tools.
              </Typography>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 4,
                maxWidth: 500,
                mx: "auto",
                border: "2px solid",
                borderColor: "primary.main",
                borderRadius: 3,
                background: alpha(theme.palette.primary.main, 0.02),
              }}
            >
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" fontWeight={600} color="primary" sx={{ mb: 1, display: "block" }}>
                    YOUR CREATOR IDENTIFIER
                  </Typography>
                  <TextField
                    fullWidth
                    value={creatorName}
                    disabled
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        fontWeight: 600,
                        fontSize: "1.1rem",
                        bgcolor: "background.paper",
                      },
                    }}
                  />
                </Box>

                <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2 }}>
                  This unique identifier is associated with payments made to you. Keep it secure!
                </Alert>

                <Box sx={{ pt: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    What happens next?
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      ✓ Complete your profile (2 minutes)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ✓ Connect your social platforms
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ✓ Secure your account with Google
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ✓ Start receiving brand offers!
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Paper>

            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleNext}
                sx={{
                  px: 6,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: "none",
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                  "&:hover": {
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                  },
                }}
              >
                Let's Get Started →
              </Button>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ py: isMobile ? 2 : 3 }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} gutterBottom>
                Build Your Profile
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Help brands discover you by sharing some basic information about yourself and your content.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: "block" }}>
                    PHONE NUMBER *
                  </Typography>
                  <MuiPhoneInput
                    country={'us'}
                    preferredCountries={['us']}
                    value={profileData.phone_number}
                    onChange={(value) => updateProfileData("phone_number", value)}
                    isValid={(value) => value.length >= 11}
                    required
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    For campaign notifications and verification
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Primary Region</InputLabel>
                  <Select
                    value={profileData.region}
                    onChange={(e) => updateProfileData("region", e.target.value)}
                    label="Primary Region"
                    sx={{ borderRadius: 2 }}
                  >
                    {regions.map((region) => (
                      <MenuItem key={region.value} value={region.value}>
                        {region.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Content Categories</InputLabel>
                  <Select
                    multiple
                    value={profileData.niche}
                    onChange={(e) => updateProfileData("niche", e.target.value)}
                    input={<OutlinedInput label="Content Categories" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={value}
                            size="small"
                            sx={{
                              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                              color: "white",
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    sx={{ borderRadius: 2 }}
                  >
                    {availableNiches.map((niche) => (
                      <MenuItem key={niche} value={niche}>
                        {niche}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Alert
              severity={isStep2Valid ? "success" : "info"}
              icon={isStep2Valid ? <CheckCircleIcon /> : <InfoIcon />}
              sx={{
                mt: 4,
                borderRadius: 2,
                bgcolor: alpha(isStep2Valid ? theme.palette.success.main : theme.palette.info.main, 0.1),
                "& .MuiAlert-icon": {
                  color: isStep2Valid ? "success.main" : "info.main",
                },
              }}
            >
              <AlertTitle sx={{ fontWeight: 600 }}>
                {isStep2Valid ? "Profile Complete!" : "Why we need this"}
              </AlertTitle>
              {isStep2Valid 
                ? "All required fields are complete. You can continue to the next step."
                : "This helps us match you with relevant brand partnerships and ensure smooth communication throughout the campaign process. You can update this anytime in your dashboard."
              }
            </Alert>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ py: isMobile ? 2 : 3 }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} gutterBottom>
                Connect Your Platforms
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Link your social media so brands can discover your content and audience.
                We'll automatically sync your follower counts and engagement metrics.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {socialPlatforms.map((platform) => (
                <Grid item xs={12} sm={6} key={platform.key}>
                  <Box>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <span>{platform.icon}</span> {platform.label.toUpperCase()}
                    </Typography>
                    <TextField
                      fullWidth
                      value={profileData.socialLinks[platform.key]}
                      onChange={(e) => updateSocialLink(platform.key, e.target.value)}
                      placeholder={platform.placeholder}
                      type="url"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Paper
              elevation={0}
              sx={{
                mt: 4,
                p: 3,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: alpha(theme.palette.info.main, 0.02),
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <InfoIcon color="info" />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Don't worry, you can skip this
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Social links help brands find you faster, but they're completely optional.
                    You can add them later in your dashboard. At least one link is recommended
                    for better visibility in our marketplace.
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ py: isMobile ? 2 : 4 }}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.3)}`,
                }}
              >
                <CelebrationIcon sx={{ fontSize: 48, color: "white" }} />
              </Box>
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} gutterBottom>
                Almost There!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: "auto" }}>
                Secure your account with Google to unlock the full Blitz experience.
                This enables automated payment processing, brand communication, and more.
              </Typography>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 4,
                maxWidth: 600,
                mx: "auto",
                border: "2px dashed",
                borderColor: "divider",
                borderRadius: 3,
              }}
            >
              <Typography variant="h6" fontWeight={600} sx={{ mb: 3, textAlign: "center" }}>
                Your Account Benefits
              </Typography>

              <Grid container spacing={2} sx={{ mb: 4 }}>
                {[
                  { icon: "🔐", text: "Secure Google authentication" },
                  { icon: "💰", text: "Automated payment processing" },
                  { icon: "📧", text: "Direct brand communication" },
                  { icon: "📊", text: "Campaign analytics dashboard" },
                  { icon: "🎯", text: "Personalized brand matches" },
                  { icon: "⚡", text: "Instant offer notifications" },
                ].map((benefit, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "1.2rem",
                        }}
                      >
                        {benefit.icon}
                      </Box>
                      <Typography variant="body2" fontWeight={500}>
                        {benefit.text}
                      </Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ textAlign: "center" }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  Complete Your Registration
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <GoogleLogin
                    size="large"
                    onSuccess={handleCredentialResponse}
                    onError={handleLoginFailure}
                    text="continue_with"
                    theme="outline"
                    shape="pill"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
                  By continuing, you agree to Blitz's Terms of Service and Privacy Policy
                </Typography>
              </Box>
            </Paper>
          </Box>
        );

      default:
        return "Unknown step";
    }
  };

  if (formIsDisabled || isError) {
    return (
      <>
        <AlertDialog alertState={dialogState} />
        <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isVerifying}>
          <CircularProgress color="inherit" />
        </Backdrop>

        <ThemeProvider theme={lightTheme}>
          <CssBaseline />
          <Box sx={{ width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
            <HomeHeader />
            <Toolbar />
            <Container maxWidth="md" sx={{ flex: 1, py: 4 }}>
              {alreadyRegistered?.registered && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  <AlertTitle sx={{ fontWeight: 600 }}>Already Claimed</AlertTitle>
                  This creator account has already been verified. If you believe this is an error,
                  please contact the Blitz support team.
                </Alert>
              )}
              {alreadyRegistered?.existsInCreatorDB === false && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  <AlertTitle sx={{ fontWeight: 600 }}>Creator Not Found</AlertTitle>
                  We couldn't locate this creator in our database. Please verify the URL or
                  contact support if you believe this is an error.
                </Alert>
              )}
              {isError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  <AlertTitle sx={{ fontWeight: 600 }}>Verification Error</AlertTitle>
                  {String(error?.message || "There was a problem verifying your status.")}
                </Alert>
              )}
            </Container>
          </Box>
        </ThemeProvider>
      </>
    );
  }

  return (
    <>
      <AlertDialog alertState={dialogState} />
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading || isVerifying}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: "background.default" }}>
          <HomeHeader />
          <Toolbar />

          {/* Header with Progress */}
          <Paper
            elevation={0}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: "white",
              py: isMobile ? 3 : 4,
              borderRadius: 0,
            }}
          >
            <Container maxWidth="lg">
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <PersonIcon sx={{ fontSize: isMobile ? 32 : 40 }} />
                <Box>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
                    Creator Onboarding
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Complete your profile to access the Blitz platform
                  </Typography>
                </Box>
              </Stack>

              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="caption" fontWeight={600}>
                    Profile Completion
                  </Typography>
                  <Typography variant="caption" fontWeight={700}>
                    {completionPercentage}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={completionPercentage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "rgba(255,255,255,0.2)",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: "white",
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
            </Container>
          </Paper>

          {/* Main Content */}
          <Container maxWidth="lg" sx={{ py: isMobile ? 3 : 5 }}>
            {/* Stepper */}
            <Stepper
              activeStep={activeStep}
              connector={<ColorlibConnector />}
              sx={{ mb: isMobile ? 3 : 5 }}
              alternativeLabel={!isMobile}
              orientation={isMobile ? "vertical" : "horizontal"}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel StepIconComponent={ColorlibStepIcon}>
                    <Typography variant="body2" fontWeight={600}>
                      {label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Step Content */}
            <Paper
              elevation={0}
              sx={{
                p: isMobile ? 3 : 5,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                minHeight: 400,
              }}
            >
              {getStepContent(activeStep)}

              {/* Navigation */}
              {activeStep !== 0 && activeStep !== 3 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 5,
                    pt: 3,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <Button
                    onClick={handleBack}
                    disabled={loading}
                    variant="outlined"
                    size="large"
                    sx={{
                      px: 4,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    ← Back
                  </Button>
                  <Button
                    onClick={handleNext}
                    disabled={loading || (activeStep === 1 && !isStep2Valid)}
                    variant="contained"
                    size="large"
                    sx={{
                      px: 4,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                      "&:hover": {
                        boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                      },
                    }}
                  >
                    Continue →
                  </Button>
                </Box>
              )}
            </Paper>
          </Container>
        </Box>
      </ThemeProvider>
    </>
  );
};

export default CreatorConnect;